/**
 * Main Application Component
 * 
 * Custom authentication flow with:
 * - Custom login page matching v3 design
 * - Cognito authentication via Amplify
 * - Protected routes for authenticated users
 * - Shared AppLayout with admin sidebar for admin users
 */

import { useState, useEffect } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import theme from './theme';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { PortalPage } from './pages/PortalPage';
import { ViewerPage } from './pages/ViewerPage';
import { LoginPage } from './pages/LoginPage';
import { UsersSection } from './pages/admin/UsersSection';
import { EmbedSettingsSection } from './pages/admin/EmbedSettingsSection';
import { SudoProvider } from './hooks/useSudo';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Loading screen shown while auth is initializing
 */
function LoadingScreen() {
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress size={48} />
      <Box sx={{ color: 'text.secondary' }}>Loading...</Box>
    </Box>
  );
}

/**
 * Main app content with routing
 */
function AppContent() {
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  useEffect(() => {
    // If we're coming back from an OAuth redirect, wait for Amplify to process it
    // The Hub listener will handle setting the auth state
    const hasOAuthCallback = window.location.search.includes('code=') || window.location.search.includes('error=');
    
    if (!hasOAuthCallback) {
      // No OAuth callback in URL, check auth state normally
      checkAuthState();
    }

    // Listen for auth events
    const hubListener = Hub.listen('auth', ({ payload }) => {
      console.log('Auth event:', payload.event);
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          console.log('User signed in successfully via OAuth');
          setAuthState('authenticated');
          // Clean up URL after OAuth callback
          if (window.location.search.includes('code=')) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          break;
        case 'signedOut':
          console.log('User signed out');
          setAuthState('unauthenticated');
          break;
        case 'tokenRefresh':
          // Token refreshed, still authenticated
          console.log('Token refreshed');
          break;
        case 'tokenRefresh_failure':
          // Token refresh failed, sign out
          console.log('Token refresh failed');
          setAuthState('unauthenticated');
          break;
        case 'customOAuthState':
          // OAuth flow started (e.g., SAML redirect)
          console.log('OAuth flow started');
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth sign-in failed:', payload.data);
          setAuthState('unauthenticated');
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, []);

  async function checkAuthState() {
    try {
      await getCurrentUser();
      setAuthState('authenticated');
    } catch {
      setAuthState('unauthenticated');
    }
  }

  if (authState === 'loading') {
    return <LoadingScreen />;
  }

  if (authState === 'unauthenticated') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Authenticated - AppLayout provides Header + sidebar (admin-only)
  return (
    <Authenticator.Provider>
      <SudoProvider>
      <BrowserRouter>
        <Routes>
          {/* AppLayout wraps all authenticated routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Available to all authenticated users */}
            <Route index element={<PortalPage />} />
            <Route path="viewer/:dashboardId" element={<ViewerPage />} />

            {/* Admin-only routes */}
            <Route
              path="users"
              element={
                <ProtectedRoute requireAdmin>
                  <Box sx={{ p: 4 }}><UsersSection /></Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="embed-settings"
              element={
                <ProtectedRoute requireAdmin>
                  <Box sx={{ p: 4 }}><EmbedSettingsSection /></Box>
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </SudoProvider>
    </Authenticator.Provider>
  );
}

/**
 * Main App component
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
